import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  PhotoIcon,
  CalendarIcon,
  TagIcon,
  MagnifyingGlassIcon,
  ArrowUpTrayIcon,
  XMarkIcon,
  SparklesIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import { getAdminAccessToken } from '../../utils/authToken';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Helper to format ISO date string to local datetime-local input value
// datetime-local inputs expect local time, but toISOString() returns UTC
const formatDateForLocalInput = (isoDate) => {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  // Use local date/time getters to format as YYYY-MM-DDTHH:mm
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const ArticleSettings = ({
  title,
  slug,
  excerpt,
  featuredImage,
  status,
  publishDate,
  seoTitle,
  seoDescription,
  categoryIds,
  sourceLanguage = 'en',
  onChange
}) => {
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showAiPrompt, setShowAiPrompt] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const token = getAdminAccessToken();
        const response = await axios.get(`${API_BASE_URL}/api/admin/blog/categories`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCategories(response.data.data?.categories || []);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB');
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const token = getAdminAccessToken();
      const response = await axios.post(
        `${API_BASE_URL}/api/assets/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.url) {
        // Convert relative URL to full URL if needed
        const imageUrl = response.data.url.startsWith('/') 
          ? `${API_BASE_URL}${response.data.url}` 
          : response.data.url;
        onChange({ featuredImage: imageUrl });
      }
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  const handleAiGenerate = async () => {
    const promptToUse = aiPrompt.trim() || `Professional blog featured image for article titled "${title}"`;
    
    setIsGenerating(true);
    setShowAiPrompt(false);

    try {
      const token = getAdminAccessToken();
      const response = await axios.post(
        `${API_BASE_URL}/api/assets/blog/generate-image`,
        {
          prompt: promptToUse,
          aspectRatio: '16:9'
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.url) {
        // Convert relative URL to full URL if needed
        const imageUrl = response.data.url.startsWith('/') 
          ? `${API_BASE_URL}${response.data.url}` 
          : response.data.url;
        onChange({ featuredImage: imageUrl });
        setAiPrompt('');
      }
    } catch (error) {
      console.error('AI generation failed:', error);
      alert('Failed to generate image. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const toggleCategory = (categoryId) => {
    const current = categoryIds || [];
    if (current.includes(categoryId)) {
      onChange({ categoryIds: current.filter(id => id !== categoryId) });
    } else {
      onChange({ categoryIds: [...current, categoryId] });
    }
  };

  return (
    <div className="space-y-6">
      {/* Source Language - First Setting */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
          <GlobeAltIcon className="w-4 h-4" />
          Source Language
        </label>
        <select
          value={sourceLanguage || 'en'}
          onChange={(e) => onChange({ sourceLanguage: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="en">🇺🇸 English (LTR)</option>
          <option value="he">🇮🇱 עברית / Hebrew (RTL)</option>
        </select>
        <p className="mt-1 text-xs text-gray-500">
          {sourceLanguage === 'he' ? 'Article will be displayed right-to-left' : 'Article will be displayed left-to-right'}
        </p>
        
        {/* Translation Info Panel */}
        <div className="mt-2 p-2 bg-blue-50 rounded-lg">
          <div className="flex items-start gap-2 text-xs text-blue-700">
            <GlobeAltIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>
              Article will be auto-translated to: Russian, Arabic, French, German, Spanish, Italian
            </span>
          </div>
        </div>
      </div>

      {/* Featured Image */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
          <PhotoIcon className="w-4 h-4" />
          Featured Image
        </label>
        
        {featuredImage ? (
          <div className="relative group">
            <img
              src={featuredImage}
              alt="Featured"
              className="w-full h-40 object-cover rounded-lg"
            />
            <button
              onClick={() => onChange({ featuredImage: '' })}
              className="absolute top-2 right-2 p-1 bg-white rounded-full shadow opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <XMarkIcon className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Upload option */}
            <label className={`flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary-400 transition-colors ${isUploading || isGenerating ? 'opacity-50 pointer-events-none' : ''}`}>
              <ArrowUpTrayIcon className="w-6 h-6 text-gray-400 mb-1" />
              <span className="text-sm text-gray-500">
                {isUploading ? 'Uploading...' : 'Upload image'}
              </span>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={isUploading || isGenerating}
                className="hidden"
              />
            </label>
            
            {/* AI Generation option */}
            {!showAiPrompt ? (
              <button
                onClick={() => setShowAiPrompt(true)}
                disabled={isUploading || isGenerating}
                className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 text-sm"
              >
                {isGenerating ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Generating...
                  </>
                ) : (
                  <>
                    <SparklesIcon className="w-4 h-4" />
                    Generate with AI
                  </>
                )}
              </button>
            ) : (
              <div className="space-y-2">
                <textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder={`Describe the image (or leave empty for auto: "${title?.substring(0, 30) || 'article'}...")`}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  rows={2}
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowAiPrompt(false)}
                    className="flex-1 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAiGenerate}
                    disabled={isGenerating}
                    className="flex-1 py-2 text-sm bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 flex items-center justify-center gap-1"
                  >
                    <SparklesIcon className="w-4 h-4" />
                    Generate
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Status & Publish Date */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            value={status || 'draft'}
            onChange={(e) => onChange({ status: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="scheduled">Scheduled</option>
          </select>
        </div>

        <div>
          <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
            <CalendarIcon className="w-4 h-4" />
            Publish Date
          </label>
          <input
            type="datetime-local"
            value={formatDateForLocalInput(publishDate)}
            onChange={(e) => onChange({ publishDate: e.target.value ? new Date(e.target.value).toISOString() : null })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Categories */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
          <TagIcon className="w-4 h-4" />
          Categories
        </label>
        
        {loadingCategories ? (
          <div className="text-sm text-gray-500">Loading categories...</div>
        ) : categories.length === 0 ? (
          <div className="text-sm text-gray-500">No categories yet. Create some in the Categories page.</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => {
              const isSelected = (categoryIds || []).includes(category.id);
              return (
                <button
                  key={category.id}
                  onClick={() => toggleCategory(category.id)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    isSelected
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category.icon && <span className="mr-1">{category.icon}</span>}
                  {category.name}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Excerpt */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Excerpt
        </label>
        <textarea
          value={excerpt || ''}
          onChange={(e) => onChange({ excerpt: e.target.value })}
          placeholder="Brief description for article previews..."
          rows={3}
          dir={sourceLanguage === 'he' ? 'rtl' : 'ltr'}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
        />
        <p className="mt-1 text-xs text-gray-500">
          {(excerpt || '').length}/160 characters recommended
        </p>
      </div>

      {/* SEO Settings */}
      <div className="border-t pt-4">
        <h3 className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-4">
          <MagnifyingGlassIcon className="w-4 h-4" />
          SEO Settings
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              SEO Title
            </label>
            <input
              type="text"
              value={seoTitle || ''}
              onChange={(e) => onChange({ seoTitle: e.target.value })}
              placeholder={title || 'Article title...'}
              dir={sourceLanguage === 'he' ? 'rtl' : 'ltr'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <p className="mt-1 text-xs text-gray-500">
              {(seoTitle || title || '').length}/60 characters
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              SEO Description
            </label>
            <textarea
              value={seoDescription || ''}
              onChange={(e) => onChange({ seoDescription: e.target.value })}
              placeholder={excerpt || 'Meta description for search engines...'}
              rows={3}
              dir={sourceLanguage === 'he' ? 'rtl' : 'ltr'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            />
            <p className="mt-1 text-xs text-gray-500">
              {(seoDescription || excerpt || '').length}/160 characters
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              URL Slug
            </label>
            <div className="flex items-center">
              <span className="text-sm text-gray-500 mr-1">/blog/</span>
              <input
                type="text"
                value={slug || ''}
                onChange={(e) => onChange({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                placeholder="article-url-slug"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleSettings;
